import CheckCircle from "@mui/icons-material/CheckCircle";
import Pending from "@mui/icons-material/PendingOutlined";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import { Box, Stack, Step, StepLabel, Stepper, Tooltip, Typography, useTheme } from "@mui/material";
import { SubmissionStatus } from "generated-graphql/graphql";
import { useWasteStepper } from "hooks/useWasteStepper";

import { StepHeader } from "./StepHeader";

type ActivityStatus = "completed" | "active" | "notStarted";

/** Icon for an activity step, matching the Tier II report's ActivityStepper. */
function ActivityStepIcon({ status }: { status: ActivityStatus }) {
  const theme = useTheme();
  if (status === "completed") {
    return <CheckCircle style={{ color: theme.palette.success.light }} />;
  }
  if (status === "active") {
    return <Pending style={{ color: theme.palette.warning.light }} />;
  }
  return <RadioButtonUnchecked color="disabled" />;
}

/**
 * Read-only landing for a frozen (submitted/filed) 8700-12 submission — the
 * waste analog of the Tier II report's "Submitted" view. Titled "Filing
 * Progress", it summarizes where the filing is and shows a vertical activity
 * stepper of the post-submission lifecycle.
 *
 * Only rendered once the submission is frozen, so "Submitted for processing" is
 * always complete; "Filing complete" is complete when filed and in-progress
 * while RCRAInfo filing is still pending.
 */
export function FilingProgress() {
  const { submission } = useWasteStepper();
  const status = submission?.status ?? SubmissionStatus.ProcessingSubmission;
  const isFilingComplete = status === SubmissionStatus.FilingComplete;

  const description = isFilingComplete
    ? "This 8700-12 notification is filing complete."
    : "This 8700-12 notification has been submitted to Encamp and is being verified and filed.";

  const steps: { title: string; status: ActivityStatus }[] = [
    { title: "Submitted for processing", status: "completed" },
    { title: "Filing complete", status: isFilingComplete ? "completed" : "active" },
  ];

  return (
    <Stack spacing={2}>
      <StepHeader title="Filing Progress" description={description} />
      <Box sx={{ maxWidth: 500 }}>
        {!isFilingComplete && (
          <Tooltip title="During this time, Encamp verifies the notification, files it with RCRAInfo, and makes any required fee payments. Reach out to Encamp Support with any questions.">
            <Typography variant="caption" color="text.secondary" fontStyle="italic">
              Estimated processing time: 2–14 days
            </Typography>
          </Tooltip>
        )}
        <Stepper orientation="vertical" sx={{ mt: 1 }}>
          {steps.map(({ title, status: stepStatus }) => (
            <Step
              key={title}
              completed={stepStatus === "completed"}
              active={stepStatus === "active"}
              expanded
            >
              <StepLabel StepIconComponent={() => <ActivityStepIcon status={stepStatus} />}>
                {title}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
    </Stack>
  );
}
